import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SchoolIcon from '@mui/icons-material/School';
import LockIcon from '@mui/icons-material/Lock';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { getCursos, postCursoParaNota, deleteCurso } from '../../../services/cursos.service';
import { getMediosInscripcion } from '../../../services/mediosInscripcion.service';
import { getPlataformasDictado } from '../../../services/plataformasDictado.service';
import { getTiposCapacitacion } from '../../../services/tiposCapacitacion.service';
import useFeedbackModal from '../../../hooks/useFeedbackModal';

// ---------------------------------------------------------------------------
// Diccionario de nombres legibles para estados de notas
// ---------------------------------------------------------------------------
const ESTADO_NOTA_NOMBRES = {
  PCARGA: 'Pendiente Suba de Nota',
  PEND: 'Pendiente de Revisión',
  AUT: 'Autorizada',
  REC: 'Rechazada',
};

/**
 * Obtiene la información de la nota de autorización vinculada al curso (si existe).
 * Determina el ID de la nota, el código de estado activo y su etiqueta legible.
 */
const getNotaVinculadaInfo = (curso) => {
  const notaId = curso?.nota_autorizacion_id || curso?.detalle_nota?.id;
  if (!notaId) return null;

  const cambios = curso?.detalle_nota?.cambios_estados;
  let estadoCod = null;
  let estadoNombre = null;

  if (Array.isArray(cambios) && cambios.length > 0) {
    const activo = cambios.find((ce) => ce.fecha_hora_hasta === null);
    const cambioActivo = activo || cambios[cambios.length - 1];
    estadoCod = cambioActivo?.estado_nota_autorizacion || null;
    estadoNombre = cambioActivo?.detalle_estado?.nombre || null;
  } else if (curso?.detalle_nota?.estado_nota_autorizacion_cod) {
    estadoCod = curso.detalle_nota.estado_nota_autorizacion_cod;
  } else if (curso?.estado_nota_autorizacion) {
    estadoCod = curso.estado_nota_autorizacion;
  }

  const codFinal = (estadoCod || 'PCARGA').toUpperCase();
  const labelFinal = estadoNombre || ESTADO_NOTA_NOMBRES[codFinal] || codFinal;

  return {
    notaId,
    estadoCod: codFinal,
    estadoNombre: labelFinal,
  };
};

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------
const DEFAULT_NEW_CURSO = {
  nombre: '',
  cupo: '',
  cantidad_horas: '',
  medio_inscripcion: '',
  plataforma_dictado: '',
  tipo_capacitacion: '',
};

// ---------------------------------------------------------------------------
// CursosSection
// ---------------------------------------------------------------------------
const CursosSection = ({ selectedCursos = [], onChange, error, containerRef, currentNotaId = null }) => {
  // Listas del servidor
  const [catalogoCursos, setCatalogoCursos] = useState([]);
  const [mediosInscripcion, setMediosInscripcion] = useState([]);
  const [plataformas, setPlataformas] = useState([]);
  const [tiposCapacitacion, setTiposCapacitacion] = useState([]);

  // UI states
  const [loadingCatalogo, setLoadingCatalogo] = useState(false);
  const [loadingLists, setLoadingLists] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [catalogoError, setCatalogoError] = useState(null);

  // Catálogo ordenado alfabéticamente en forma ascendente
  const sortedCatalogoCursos = useMemo(() => {
    return [...catalogoCursos].sort((a, b) =>
      (a.nombre || '').localeCompare(b.nombre || '', 'es', { numeric: true, sensitivity: 'base' })
    );
  }, [catalogoCursos]);

  // Filtro de solo opciones vigentes (esVigente === 1, true o null/no definido si la entidad no maneja borrado lógico)
  const isItemVigente = (item) => {
    if (item.esVigente === undefined || item.esVigente === null) return true;
    return item.esVigente === 1 || item.esVigente === true || item.esVigente === '1';
  };

  const mediosInscripcionVigentes = useMemo(() => {
    return mediosInscripcion.filter(isItemVigente);
  }, [mediosInscripcion]);

  const plataformasVigentes = useMemo(() => {
    return plataformas.filter(isItemVigente);
  }, [plataformas]);

  const tiposCapacitacionVigentes = useMemo(() => {
    return tiposCapacitacion.filter(isItemVigente);
  }, [tiposCapacitacion]);

  // Dialog crear nuevo curso
  const [openDialog, setOpenDialog] = useState(false);
  const [newCurso, setNewCurso] = useState(DEFAULT_NEW_CURSO);
  const [newCursoErrors, setNewCursoErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // ---------------------------------------------------------------------------
  // Carga inicial de catálogo
  // ---------------------------------------------------------------------------
  const fetchCatalogo = useCallback(async (q = '') => {
    setLoadingCatalogo(true);
    setCatalogoError(null);
    try {
      const data = await getCursos(q, 'NVIG');
      const cursosList = Array.isArray(data) ? data : [];
      setCatalogoCursos(cursosList);
      if (cursosList.length === 0 && !q) {
        setCatalogoError('EMPTY');
      }
    } catch {
      setCatalogoError('ERROR');
    } finally {
      setLoadingCatalogo(false);
    }
  }, []);

  useEffect(() => {
    fetchCatalogo();
  }, [fetchCatalogo]);

  // ---------------------------------------------------------------------------
  // Carga listas para "Crear Nuevo Curso" al abrir el dialog
  // ---------------------------------------------------------------------------
  const fetchFormLists = useCallback(async () => {
    if (mediosInscripcion.length > 0) return; // ya cargadas
    setLoadingLists(true);
    try {
      const [mis, plats, tipos] = await Promise.all([
        getMediosInscripcion(),
        getPlataformasDictado(),
        getTiposCapacitacion(),
      ]);
      setMediosInscripcion(Array.isArray(mis) ? mis : []);
      setPlataformas(Array.isArray(plats) ? plats : []);
      setTiposCapacitacion(Array.isArray(tipos) ? tipos : []);
    } catch {
      // Se muestran vacíos; el usuario verá los selects sin opciones
    } finally {
      setLoadingLists(false);
    }
  }, [mediosInscripcion.length]);

  const handleOpenDialog = () => {
    fetchFormLists();
    setOpenDialog(true);
    setSaveError(null);
  };

  // ---------------------------------------------------------------------------
  // Búsqueda con debounce leve
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const timer = setTimeout(() => fetchCatalogo(busqueda), 400);
    return () => clearTimeout(timer);
  }, [busqueda, fetchCatalogo]);

  const { showConfirm, showSuccess, showError } = useFeedbackModal();

  const handleToggleCurso = (curso) => {
    const notaInfo = getNotaVinculadaInfo(curso);
    const isLinkedToNonAutNota = Boolean(
      notaInfo &&
      notaInfo.estadoCod !== 'AUT' &&
      (!currentNotaId || Number(currentNotaId) !== Number(notaInfo.notaId))
    );
    if (isLinkedToNonAutNota) return;

    const ya = selectedCursos.find((c) => c.cod === curso.cod);
    if (ya) {
      onChange(selectedCursos.filter((c) => c.cod !== curso.cod));
    } else {
      onChange([...selectedCursos, curso]);
    }
  };

  const handleRemoveCurso = (cod) => {
    onChange(selectedCursos.filter((c) => c.cod !== cod));
  };

  const handleDeleteCurso = async (e, curso) => {
    e.stopPropagation();
    const confirmed = await showConfirm({
      title: '¿Eliminar curso?',
      description: `Se eliminará de forma permanente el curso "${curso.nombre}". Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
    });

    if (!confirmed) return;

    try {
      await deleteCurso(curso.cod);
      // Si el curso estaba entre los seleccionados, removerlo
      if (selectedCursos.some((c) => c.cod === curso.cod)) {
        onChange(selectedCursos.filter((c) => c.cod !== curso.cod));
      }
      await fetchCatalogo(busqueda);
      await showSuccess({
        title: 'Curso eliminado',
        description: `El curso "${curso.nombre}" fue eliminado correctamente.`,
      });
    } catch (err) {
      await showError({
        title: 'Error al eliminar',
        description: err.message || 'No se pudo eliminar el curso.',
      });
    }
  };

  // ---------------------------------------------------------------------------
  // Crear nuevo curso
  // ---------------------------------------------------------------------------
  const isElearningTipoCapacitacion = (codTipo) => {
    if (!codTipo) return false;
    const tipo = tiposCapacitacion.find((t) => t.cod === codTipo);
    const nombre = (tipo?.nombre || '').toLowerCase();
    const cod = (codTipo || '').toLowerCase();
    return (
      cod === 'el' ||
      cod === 'elearning' ||
      cod === 'e-learning' ||
      nombre.includes('e-learning') ||
      nombre.includes('elearning')
    );
  };

  const validateNewCurso = (cursoValues = newCurso) => {
    const errs = {};
    if (!cursoValues.nombre.trim()) errs.nombre = 'El nombre es obligatorio';
    if (!cursoValues.medio_inscripcion) errs.medio_inscripcion = 'Seleccione un medio de inscripción';
    if (!cursoValues.plataforma_dictado) errs.plataforma_dictado = 'Seleccione una plataforma de dictado';
    if (!cursoValues.tipo_capacitacion) errs.tipo_capacitacion = 'Seleccione un tipo de capacitación';

    const cupoNum = Number(cursoValues.cupo);
    if (!cursoValues.cupo || isNaN(cupoNum) || !Number.isInteger(cupoNum) || cupoNum < 1) {
      errs.cupo = 'El cupo debe ser un entero positivo mayor o igual a 1';
    }

    const horasNum = Number(cursoValues.cantidad_horas);
    const esElearning = isElearningTipoCapacitacion(cursoValues.tipo_capacitacion);
    const minHoras = esElearning ? 8 : 1;

    if (!cursoValues.cantidad_horas || isNaN(horasNum) || !Number.isInteger(horasNum) || horasNum < minHoras) {
      errs.cantidad_horas = esElearning
        ? 'Para e-learning la cantidad de horas debe ser mayor o igual a 8'
        : 'La cantidad de horas debe ser un entero mayor o igual a 1';
    }

    return errs;
  };

  const handleCreateCurso = async () => {
    const errs = validateNewCurso();
    if (Object.keys(errs).length > 0) {
      setNewCursoErrors(errs);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        nombre: newCurso.nombre,
        cupo: parseInt(newCurso.cupo, 10),
        cantidad_horas: parseInt(newCurso.cantidad_horas, 10),
        medio_inscripcion: newCurso.medio_inscripcion,
        plataforma_dictado: newCurso.plataforma_dictado,
        tipo_capacitacion: newCurso.tipo_capacitacion,
      };

      const created = await postCursoParaNota(payload);

      // Agregar al listado seleccionado y al catálogo local
      onChange([...selectedCursos, created]);
      setCatalogoCursos((prev) => [created, ...prev]);

      setOpenDialog(false);
      setNewCurso(DEFAULT_NEW_CURSO);
      setNewCursoErrors({});
    } catch (err) {
      setSaveError(err?.message || 'Error al crear el curso. Intente nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleNewCursoChange = (field) => (e) => {
    let value = e.target.value;
    if (field === 'cupo' || field === 'cantidad_horas') {
      value = value.replace(/\D/g, '');
    }

    const nextCurso = { ...newCurso, [field]: value };
    setNewCurso(nextCurso);

    setNewCursoErrors((prev) => {
      const nextErrors = { ...prev };
      if (nextErrors[field]) {
        nextErrors[field] = null;
      }

      // Si cambia el tipo de capacitación o las horas, validar dinámicamente cantidad_horas
      if (field === 'tipo_capacitacion' || field === 'cantidad_horas') {
        const horasVal = nextCurso.cantidad_horas;
        if (horasVal) {
          const horasNum = Number(horasVal);
          const esElearning = isElearningTipoCapacitacion(nextCurso.tipo_capacitacion);
          const minHoras = esElearning ? 8 : 1;
          if (isNaN(horasNum) || !Number.isInteger(horasNum) || horasNum < minHoras) {
            nextErrors.cantidad_horas = esElearning
              ? 'Para e-learning la cantidad de horas debe ser mayor o igual a 8'
              : 'La cantidad de horas debe ser un entero mayor o igual a 1';
          } else {
            nextErrors.cantidad_horas = null;
          }
        }
      }

      return nextErrors;
    });
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Card ref={containerRef} variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon color="primary" />
            <Typography variant="h6" color="primary">
              Cursos <span style={{ color: '#E2464C' }}>*</span>
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Crear Nuevo Curso
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Seleccione uno o más cursos existentes. También puede crear uno nuevo desde el botón superior.
        </Typography>

        {/* Chips de cursos seleccionados */}
        {selectedCursos.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {selectedCursos.map((c) => (
              <Chip
                key={c.cod}
                label={c.nombre}
                color="primary"
                variant="filled"
                onDelete={() => handleRemoveCurso(c.cod)}
                sx={{ fontWeight: 500 }}
              />
            ))}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Buscador + listado del catálogo */}
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label="Buscar curso..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          sx={{ mb: 1.5 }}
        />

        {loadingCatalogo ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : catalogoError === 'EMPTY' ? (
          <Alert severity="info">
            No hay cursos disponibles para seleccionar, tiene la opción de <strong>"CREAR NUEVO CURSO"</strong>.
          </Alert>
        ) : catalogoError ? (
          <Alert
            severity="warning"
            action={
              <Button size="small" onClick={() => fetchCatalogo(busqueda)}>
                Reintentar
              </Button>
            }
          >
            Cursos no encontrados
          </Alert>
        ) : (
          <Box
            sx={{
              maxHeight: 220,
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
            }}
          >
            {sortedCatalogoCursos.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                No se encontraron cursos{busqueda ? ` para "${busqueda}"` : ''}.
              </Typography>
            ) : (
              sortedCatalogoCursos.map((curso, idx) => {
                const isSelected = selectedCursos.some((c) => c.cod === curso.cod);
                const notaInfo = getNotaVinculadaInfo(curso);
                const isLinkedToNonAutNota = Boolean(
                  notaInfo &&
                  notaInfo.estadoCod !== 'AUT' &&
                  (!currentNotaId || Number(currentNotaId) !== Number(notaInfo.notaId))
                );

                const tooltipTitle = isLinkedToNonAutNota
                  ? `No disponible para selección: este curso se encuentra vinculado a la Nota de Autorización N° ${notaInfo.notaId} en estado "${notaInfo.estadoNombre}" (${notaInfo.estadoCod}). Solo pueden seleccionarse cursos sin nota vinculada o con nota Autorizada (AUT).`
                  : '';

                const rowContent = (
                  <Box
                    onClick={isLinkedToNonAutNota ? undefined : () => handleToggleCurso(curso)}
                    sx={{
                      px: 2,
                      py: 1.2,
                      cursor: isLinkedToNonAutNota ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: isLinkedToNonAutNota
                        ? 'action.hover'
                        : isSelected
                          ? 'rgba(25, 118, 210, 0.08)'
                          : '#fff',
                      color: isLinkedToNonAutNota ? 'text.disabled' : 'rgba(0, 0, 0, 0.87)',
                      opacity: isLinkedToNonAutNota ? 0.75 : 1,
                      '&:hover': {
                        backgroundColor: isLinkedToNonAutNota
                          ? 'action.hover'
                          : isSelected
                            ? 'rgba(25, 118, 210, 0.14)'
                            : 'rgba(0, 0, 0, 0.04)',
                      },
                      transition: 'background-color 0.15s, opacity 0.15s',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, mr: 1 }}>
                      {isLinkedToNonAutNota && (
                        <LockIcon
                          sx={{
                            fontSize: 16,
                            color: 'text.disabled',
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        fontWeight={isSelected ? 600 : 400}
                        sx={{
                          color: isLinkedToNonAutNota ? 'text.secondary' : 'rgba(0, 0, 0, 0.87)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {curso.nombre}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      {isLinkedToNonAutNota && (
                        <Chip
                          label={`Nota N° ${notaInfo.notaId} (${notaInfo.estadoCod})`}
                          size="small"
                          variant="outlined"
                          color="warning"
                          sx={{
                            fontSize: '0.72rem',
                            height: 22,
                            cursor: 'not-allowed',
                            fontWeight: 500,
                          }}
                        />
                      )}
                      {isSelected && (
                        <Chip label="Seleccionado" size="small" color="primary" variant="outlined" />
                      )}
                      {curso.cod?.startsWith('TMP-') && curso.estado === 'NVIG' && !curso.nota_autorizacion_id && (
                        <Tooltip title="Eliminar curso recién creado" arrow>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => handleDeleteCurso(e, curso)}
                            sx={{
                              p: 0.5,
                              '&:hover': {
                                backgroundColor: 'error.lighter',
                              },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                );

                return (
                  <Box key={curso.cod}>
                    {isLinkedToNonAutNota ? (
                      <Tooltip title={tooltipTitle} arrow placement="top">
                        <Box sx={{ display: 'block', width: '100%' }}>
                          {rowContent}
                        </Box>
                      </Tooltip>
                    ) : (
                      rowContent
                    )}
                    {idx < sortedCatalogoCursos.length - 1 && <Divider />}
                  </Box>
                );
              })
            )}
          </Box>
        )}
      </CardContent>

      {/* ------------------------------------------------------------------ */}
      {/* Dialog: Crear Nuevo Curso                                           */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Crear Nuevo Curso</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Los campos marcados con (*) son obligatorios.
            </Typography>

            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}

            <Grid container spacing={2.5}>
              {/* Nombre */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Nombre del Curso"
                  value={newCurso.nombre}
                  onChange={handleNewCursoChange('nombre')}
                  error={!!newCursoErrors.nombre}
                  helperText={newCursoErrors.nombre}
                />
              </Grid>

              {/* Plataforma de Dictado */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required error={!!newCursoErrors.plataforma_dictado}>
                  <InputLabel>Plataforma de Dictado</InputLabel>
                  <Select
                    value={newCurso.plataforma_dictado}
                    label="Plataforma de Dictado"
                    onChange={handleNewCursoChange('plataforma_dictado')}
                    disabled={loadingLists}
                  >
                    {loadingLists ? (
                      <MenuItem disabled>Cargando...</MenuItem>
                    ) : (
                      plataformasVigentes.map((p) => (
                        <MenuItem key={p.cod} value={p.cod}>
                          {p.nombre}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {newCursoErrors.plataforma_dictado && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {newCursoErrors.plataforma_dictado}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Medio de Inscripción */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required error={!!newCursoErrors.medio_inscripcion}>
                  <InputLabel>Medio de Inscripción</InputLabel>
                  <Select
                    value={newCurso.medio_inscripcion}
                    label="Medio de Inscripción"
                    onChange={handleNewCursoChange('medio_inscripcion')}
                    disabled={loadingLists}
                  >
                    {loadingLists ? (
                      <MenuItem disabled>Cargando...</MenuItem>
                    ) : (
                      mediosInscripcionVigentes.map((m) => (
                        <MenuItem key={m.cod} value={m.cod}>
                          {m.nombre}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {newCursoErrors.medio_inscripcion && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {newCursoErrors.medio_inscripcion}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Tipo de Capacitación */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required error={!!newCursoErrors.tipo_capacitacion}>
                  <InputLabel>Tipo de Capacitación</InputLabel>
                  <Select
                    value={newCurso.tipo_capacitacion}
                    label="Tipo de Capacitación"
                    onChange={handleNewCursoChange('tipo_capacitacion')}
                    disabled={loadingLists}
                  >
                    {loadingLists ? (
                      <MenuItem disabled>Cargando...</MenuItem>
                    ) : (
                      tiposCapacitacionVigentes.map((t) => (
                        <MenuItem key={t.cod} value={t.cod}>
                          {t.nombre}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {newCursoErrors.tipo_capacitacion && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {newCursoErrors.tipo_capacitacion}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Cupo */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Cupo"
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  onKeyDown={(e) => {
                    if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  value={newCurso.cupo}
                  onChange={handleNewCursoChange('cupo')}
                  error={!!newCursoErrors.cupo}
                  helperText={newCursoErrors.cupo}
                />
              </Grid>

              {/* Cantidad de Horas */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Cantidad de Horas"
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  onKeyDown={(e) => {
                    if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  value={newCurso.cantidad_horas}
                  onChange={handleNewCursoChange('cantidad_horas')}
                  error={!!newCursoErrors.cantidad_horas}
                  helperText={
                    newCursoErrors.cantidad_horas ||
                    (isElearningTipoCapacitacion(newCurso.tipo_capacitacion)
                      ? 'Mínimo 8 horas requeridas para e-learning'
                      : undefined)
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            color="inherit"
            onClick={() => {
              setOpenDialog(false);
              setNewCurso(DEFAULT_NEW_CURSO);
              setNewCursoErrors({});
              setSaveError(null);
            }}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateCurso}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? 'Guardando...' : 'Crear y Agregar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default CursosSection;
